/*eslint-disable operator-linebreak*/
import {
  addMinutes,
  formatDistance,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  parseISO,
  subMinutes,
} from 'date-fns';
import padStart from 'lodash/padStart';
import moment, { Moment } from 'moment';
import { TFunction } from 'react-i18next';

import { debugNowTime } from '../hooks/useAutoRefresh';
import { dateFNSLocale } from './date';

interface Dates {
  startDate: string;
  endDate: string;
}
interface DateOptions {
  minutesBefore: number;
  minutesAfter: number;
}

export function isNow({ startDate, endDate }: Dates, options: DateOptions): boolean {
  const { minutesBefore = 5, minutesAfter = 5 } = options || {};
  const now = new Date();

  return (
    isBefore(parseISO(startDate), addMinutes(now, minutesBefore)) &&
    isAfter(parseISO(endDate), subMinutes(now, minutesAfter))
  );
}

export function isUpcoming({ startDate }: { startDate: string }): boolean {
  const now = new Date();
  return isAfter(parseISO(startDate), now);
}

export function isFinished({ endDate }: { endDate: string }): boolean {
  const now = new Date();
  return isBefore(parseISO(endDate), now);
}

export function startsToday({ startDate }: { startDate: string }): boolean {
  return isToday(parseISO(startDate));
}

export function getWorkshopStatus(workshop: Dates, options: DateOptions): string {
  if (isFinished({ endDate: workshop.endDate })) return 'finished';
  if (isNow(workshop, options)) return 'now-soon';
  if (isTomorrow(parseISO(workshop.startDate))) return 'tomorrow';
  if (isUpcoming(workshop)) return 'upcoming';
  return 'upcoming';
}

export function fromNow(date: string): string | null {
  const now = debugNowTime || new Date();
  if (!date) return null;
  return formatDistance(parseISO(date), now, {
    addSuffix: true,
    locale: dateFNSLocale(),
  });
}

export function formatTimezoneDate(date: string, timezone: string): Moment | null {
  if (!date) return null;
  if (timezone) return moment(parseISO(date)).tz(timezone);
  return moment(parseISO(date));
}

export function durationInMinutes(startDate: string, endDate: string): number {
  return Math.floor((parseISO(endDate).getTime() - parseISO(startDate).getTime()) / (60 * 1000));
}

export function roundMinutesToQuarter(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

function formatDurationMinutes(minutes: number, t: TFunction<'translation'>) {
  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes - hours * 60;
  return t('dates.duration', { hours, minutes: padStart(minutesLeft.toString(), 2, '0') });
}

export function formatDateRangeDuration(
  startDate: string,
  endDate: string,
  t: TFunction<'translation'>,
): string | null {
  if (!startDate || !endDate) return null;
  const minutes = durationInMinutes(startDate, endDate);
  return formatDurationMinutes(minutes, t);
}

export const formatDateAgo = (
  date: Date | string,
  t: TFunction<'translation'>,
  long?: boolean,
): string | null => {
  const currentDate = moment();
  const formattedDate = moment(date);

  const diffMin = { key: 'min', value: currentDate.diff(formattedDate, 'm') };
  const diffHours = { key: 'hour', value: currentDate.diff(formattedDate, 'h') };
  const diffDays = { key: 'day', value: currentDate.diff(formattedDate, 'd') };
  const diffMonth = { key: 'month', value: currentDate.diff(formattedDate, 'M') };
  const diffYear = { key: 'year', value: currentDate.diff(formattedDate, 'y') };

  const timeDiff = [diffYear, diffMonth, diffDays, diffHours, diffMin];

  const relativeTime = timeDiff.find((diff) => diff.value > 0);

  return relativeTime
    ? t(`common.${long ? 'long-' : ''}relative-time.${relativeTime.key}`, {
        count: relativeTime.value,
      })
    : t('common.relative-time.min', { count: 1 });
};

export function convertMomentToDateFns(format: string): string {
  const formatMap: Record<string, string> = {
    // Year
    'YYYY': 'yyyy',
    'YY': 'yy',

    // Month
    'MMMM': 'MMMM',
    'MMM': 'MMM',
    'MM': 'MM',
    'M': 'M',

    // Day of Month
    'DD': 'dd',
    'D': 'd',
    'do': 'dd',

    // Day of Week
    'dddd': 'EEEE',
    'ddd': 'EEE',
    // dd: 'EE', // moment: 'Mo', date-fns: 'Mo'

    // Hour
    'HH': 'HH',
    'H': 'H',
    'hh': 'hh',
    'h': 'h',

    // Minute
    'mm': 'mm',
    'm': 'm',

    // Second
    'ss': 'ss',
    's': 's',

    // AM/PM
    'A': 'a',
    'a': 'aaaa',

    // Timezone
    'Z': 'XXX',
    'ZZ': 'xx',

    // Hour string
    '[h]': "'h'",
  };

  // Trier les tokens par longueur décroissante pour éviter les collisions de remplacement
  const sortedTokens = Object.keys(formatMap).sort((a, b) => b.length - a.length);

  //already datefns return format
  if (format.includes('EE')) {
    if (format.includes('do')) {
      // fix legacy error with do :/
      return format.replace('do', 'dd');
    }
    return format;
  }

  let result = '';
  let i = 0;

  while (i < format.length) {
    let matched = false;

    for (let j = 0; j < sortedTokens.length; j++) {
      const token = sortedTokens[j];
      if (format.startsWith(token, i)) {
        result += formatMap[token];
        i += token.length;
        matched = true;
        break;
      }
    }

    if (!matched) {
      // Si aucun token ne correspond, on copie le caractère tel quel
      result += format[i];
      i++;
    }
  }

  return result;
}

// Detect if a format string is for date-fns or moment
export type DateFormatKind = 'date-fns' | 'moment' | 'unknown';

const hasAnyToken = (format: string, tokens: string[]): boolean =>
  tokens.some((token) => format.includes(token));

export function isMomentFormat(format: string): boolean {
  // Distinctive Moment tokens
  const momentDistinctTokens = [
    'YYYY',
    'DD',
    'dddd',
    'A',
    'Z',
    'ZZ',
    'LL',
    'ddd',
    'Do',
    '[h]',
    '[:]',
    'LT z',
  ];
  return hasAnyToken(format, momentDistinctTokens);
}

export function isDateFnsFormat(format: string): boolean {
  // Distinctive date-fns tokens
  const dateFnsDistinctTokens = [
    'yyyy',
    'do',
    'EEEE',
    'EE',
    'XXX',
    'xx',
    'PP',
    'p',
    'd',
    "'h'",
    "':'",
  ];
  return hasAnyToken(format, dateFnsDistinctTokens);
}

export function detectDateFormatKind(format: string): DateFormatKind {
  const isMoment = isMomentFormat(format);
  const isDateFns = isDateFnsFormat(format);

  if (isMoment && !isDateFns) return 'moment';
  if (isDateFns && !isMoment) return 'date-fns';

  // Tie-breaker for rare mixed strings
  if (format.includes('ddd Do')) return 'moment';
  if (format.includes('yyyy') || format.includes('EEEE')) return 'date-fns';
  if (format.includes('YYYY') || format.includes('dddd')) return 'moment';
  return 'unknown';
}
